import { FC, useCallback } from 'react'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import { useTranslation } from 'next-i18next'
import { Button } from '@corratech/pylot-ui'
import { UseFormGetValues } from 'react-hook-form'
import {
    Wishlist,
    ConfigurableWishlistItem,
    CartItemInput
} from '@pylot-data/pylotschema'
import { useAddToCartFromWishlist } from '../../Hooks/use-add-to-cart-from-wishlist'
import { getUidForConfigProducts } from '@corratech/pylot-wishlist/src/utils/getUidForConfigProducts'

export interface WishlistAddItemArgs {
    cartItems: Array<CartItemInput>
    removeItems: Array<{ id: string; productId: string }>
}

export type Props = {
    wishlist?: Wishlist
    getValues: UseFormGetValues<any>
    buttonLabel?: string
    item?: WishlistAddItemArgs
    index?: number
    isValid?: boolean
}

export const AddProductsToCart: FC<Props> = ({
    wishlist,
    item,
    buttonLabel,
    getValues,
    index,
    isValid = true
}) => {
    const { t } = useTranslation(['wishlist'])
    const { openToast } = useUI()
    const { addToCartFromWishlist, isLoading } = useAddToCartFromWishlist()
    const getWishlistAddItemArgs = useCallback((): WishlistAddItemArgs => {
        const itemsArray: WishlistAddItemArgs = {
            cartItems: [],
            removeItems: []
        }
        const items = wishlist!.items_v2
            ?.items as Array<ConfigurableWishlistItem>

        //get all the products for adding to cart from wishlist
        items?.forEach((wishlistItem, key) => {
            itemsArray.cartItems.push({
                sku: wishlistItem?.product?.sku!,
                selected_options: getUidForConfigProducts({
                    product: wishlistItem?.product,
                    options: wishlistItem?.bundle_options
                }),
                quantity:
                    getValues(String(key))?.quantity || wishlistItem?.quantity
            })
            itemsArray.removeItems.push({
                id: wishlistItem?.id,
                productId: String(wishlistItem?.product?.id)
            })
        })

        return itemsArray
    }, [wishlist?.id, wishlist?.items_v2])

    const handleAddToCart = () => {
        try {
            const addItemsInput = item
                ? {
                      ...item,
                      ...{
                          cartItems: [
                              {
                                  ...item.cartItems[0],
                                  ...{
                                      quantity: getValues(String(index))
                                          ?.quantity
                                  }
                              }
                          ]
                      }
                  }
                : getWishlistAddItemArgs()

            addToCartFromWishlist(addItemsInput?.cartItems, {
                wishlistId: wishlist!.id!,
                wishlistItemsIds: addItemsInput?.removeItems
            })
        } catch (e) {
            openToast(
                t('Something went wrong. Please try again'),
                ToastType.Danger
            )
        }
    }

    return (
        <Button
            type="button"
            variant="primary"
            className="btn-sm"
            loading={isLoading}
            disabled={isLoading || !isValid}
            onClick={handleAddToCart}
        >
            {buttonLabel}
        </Button>
    )
}
